<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="900px"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        icon 
        v-on="on"
      >
        <v-icon color="primary">
          mdi-pencil
        </v-icon>
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Claim Type"
      text="Edit Claim Type"
      class="mb-0"
    >
      <form v-if="dialog">
        <v-container>
          <v-row no-gutters>
            <v-col cols="12">
              <v-text-field
                v-model="localClaimType.name"
                v-validate="'required'"
                :error-messages="errors.collect('name')"
                data-vv-name="name"                
                label="Name"
              />
            </v-col>

            <v-col cols="12">
              <v-autocomplete 
                v-model="localClaimType.expense_type"
                v-validate=" 'required'"
                :items="expenseTypes"
                :error-messages="errors.collect('expenseType')"
                data-vv-name="expenseType"      
                data-vv-as="expense type"
                label="Expense Type"
                item-text="name"
                item-value="name"      
              >
                <template v-slot:item="data">
                  <v-list-item-content>
                    <div class="font-weight-bold">
                      {{ data.item.name }}
                    </div>
                    <div>{{ data.item.description }}</div>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col cols="12">
              <v-autocomplete 
                v-model="localClaimType.claimed_by"
                v-validate=" 'required'"
                :items="claimModels"
                :error-messages="errors.collect('claimedBy')"
                data-vv-name="claimedBy"      
                data-vv-as="claim by"
                label="Claim By"
                item-text="name"
                item-value="name"      
              />
            </v-col>

            <v-col
              v-if="localClaimType.expense_type === 'Mileage' || localClaimType.expense_type === 'Per unit allowance'"
              cols="12"
              class="py-0 my-0"
            >
              <v-divider />
              <v-row>
                <v-col>
                  <span class="font-weight-bold pr-2">Claim Type Details:</span>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on: tooltip }">
                      <v-icon
                        color="primary"
                        v-on="{ ...dialog, ...tooltip }"
                        @click="addRow"
                      >
                        mdi-plus
                      </v-icon>
                    </template>
                    <span>Add new detail</span>
                  </v-tooltip>
                </v-col>
              </v-row>

              <v-row
                v-for="(detail,counter) in localClaimType.details"
                :key="counter"
                class="align-center pb-4"
              >
                <v-col
                  cols="5"
                  class="py-0"
                >
                  <v-text-field
                    v-model="detail.name"
                    v-validate="'required'"
                    :name="'name' + counter"
                    :error-messages="errors.collect('name' + counter)"
                    data-vv-delay="500"
                    data-vv-as="detail"
                    placeholder="Detail (Requirement / Type)"
                  />
                </v-col>
                <v-col
                  cols="3"
                  class="py-0"
                >
                  <v-text-field
                    v-model="detail.amount"
                    v-money="money"
                    :name="'amount' + counter"
                    :error-messages="errors.collect('amount' + counter)"
                    data-vv-delay="500"  
                    data-vv-name="amount"
                    label="Amount"
                    prefix="RM"
                  />
                </v-col>
                <v-col
                  cols="3"
                  class="py-0"
                >
                  <v-autocomplete
                    v-model="detail.unit_of_measurement"
                    v-validate="'required'"
                    :name="'unit_of_measurement' + counter"
                    :error-messages="errors.collect('unit_of_measurement' + counter)"
                    data-vv-delay="500"
                    data-vv-as="unit"
                    label="Unit"
                    prefix="/"
                    :items="filteredUnits"
                    item-text="value"
                    item-value="value"
                    autocomplete="off"
                  />
                </v-col>

                <v-col class="text-center">
                  <v-icon
                    color="error"
                    @click="deleteRow(counter)"
                  >
                    mdi-window-close
                  </v-icon>
                </v-col>
              </v-row>
              <v-divider />
            </v-col>
          
            <v-col
              cols="4"
              class="pt-2"
            >
              <span> Requires document? </span>
              <v-radio-group
                v-model="localClaimType.requires_document"
                class="mt-0"
                row
              >
                <v-radio
                  label="No"
                  :value="0"
                />
                <v-radio
                  label="Yes"
                  :value="1"
                />
              </v-radio-group>
            </v-col>

            <v-col
              cols="4"
              class="pt-2"
            >
              <span> Requires confirmation? </span>
              <v-radio-group
                v-model="localClaimType.requires_confirmation"
                class="mt-0"
                row
              >
                <v-radio
                  label="No"
                  :value="0"
                />
                <v-radio
                  label="Yes"
                  :value="1"
                />
              </v-radio-group>
            </v-col>

            <v-col
              cols="4"
              class="pt-2"
            >
              <span> Requires receipt? </span>
              <v-radio-group
                v-model="localClaimType.requires_receipt"
                class="mt-0"
                row
              >
                <v-radio
                  label="No"
                  :value="0"
                />
                <v-radio
                  label="Yes"
                  :value="1"
                />
              </v-radio-group>
            </v-col>

            <v-col
              cols="4"
              class="py-0 my-0"
            >
              <span> Is description mandatory? </span>
              <v-radio-group
                v-model="localClaimType.is_description_mandatory"
                class="mt-0"
                row
              >
                <v-radio
                  label="No"
                  :value="0"
                />
                <v-radio
                  label="Yes"
                  :value="1"
                />
              </v-radio-group>
            </v-col>

            <v-col
              cols="4"
              class="py-0 my-0"
            >
              <span> Has limit? </span>
              <v-radio-group
                v-model="localClaimType.has_limit"
                class="mt-0"
                row
              >
                <v-radio
                  label="No"
                  :value="0"
                />
                <v-radio
                  label="Yes"
                  :value="1"
                />
              </v-radio-group>
            </v-col>

            <v-col
              v-if="localClaimType.has_limit"
              cols="4"
              class="py-0 my-0"
            >
              <span> Allow exceed limit? </span>
              <v-radio-group
                v-model="localClaimType.allow_exceed_limit"
                class="mt-0"
                row
              >
                <v-radio
                  label="No"
                  :value="0"
                />
                <v-radio
                  label="Yes"
                  :value="1"
                />
              </v-radio-group>
            </v-col>

            <v-col
              v-if="localClaimType.requires_receipt"
              cols="4"
              class="py-0 my-0"
            >
              <span> Allow advance purchase? </span>
              <v-radio-group
                v-model="localClaimType.allow_advance_purchase"
                class="mt-0"
                row
              >
                <v-radio
                  label="No"
                  :value="0"
                />
                <v-radio
                  label="Yes"
                  :value="1"
                />
              </v-radio-group>
            </v-col>

            <v-col
              cols="4"
              class="py-0 my-0"
            >
              <span> Allow higher superior approval? </span>
              <v-radio-group
                v-model="localClaimType.allow_higher_level_approval"
                class="mt-0"
                row
              >
                <v-radio
                  label="No"
                  :value="0"
                />
                <v-radio
                  label="Yes"
                  :value="1"
                />
              </v-radio-group>
            </v-col>

            <v-col
              cols="4"
              class="py-0 my-0"
            >
              <span> Requires declaration? </span>
              <v-radio-group
                v-model="localClaimType.requires_declaration"
                class="mt-0"
                row
              >
                <v-radio
                  label="No"
                  :value="0"
                />
                <v-radio
                  label="Yes"
                  :value="1"
                />
              </v-radio-group>
            </v-col>

            <v-col
              v-if="localClaimType.requires_declaration"
              cols="12"
              class="pb-8"
            >
              <tiptap-vuetify
                v-model="localClaimType.declaration_text"
                v-validate="'required'"
                :name="localClaimType.declaration_text"
                class="full-width text-editor-toolbar-height pb-1"
                placeholder="Declaration Text"
                :extensions="extensions"
              />
              <span 
                v-if="errors.has('declarationText')"
                class="red--text" 
              >
                {{ errors.first('declarationText') }}
              </span>
            </v-col>

            <v-col
              cols="12"
              class="py-0 my-0"
            >
              <v-divider />
              <v-row>
                <v-col>
                  <span class="font-weight-bold pr-2">Custom fields (Collect additional information for this claim type):</span>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on: tooltip }">
                      <v-icon
                        color="primary"
                        v-on="{ ...dialog, ...tooltip }"
                        @click="addCustomFieldRow"
                      >
                        mdi-plus
                      </v-icon>
                    </template>
                    <span>Add new custom field</span>
                  </v-tooltip>
                </v-col>
              </v-row>

              <v-row
                v-for="(field,counter) in localClaimType.custom_fields"
                :key="counter"
                class="align-center"
              >
                <v-col
                  cols="4"
                  class="py-0"
                >
                  <v-text-field
                    v-model="field.name"
                    v-validate="'required'"
                    :name="'field_name' + counter"
                    :error-messages="errors.collect('field_name' + counter)"
                    data-vv-delay="500"
                    data-vv-as="title"
                    placeholder="Title"
                    autocomplete="off"
                  />
                </v-col>
                <v-col
                  cols="2"
                  class="py-0"
                >
                  <v-checkbox
                    v-model="field.is_mandatory"
                    :name="'is_mandatory' + counter"
                    color="green"
                    label="Mandatory?"
                  />
                </v-col>
                <v-col
                  cols="5"
                  class="py-0 ml-4"
                >
                  <v-autocomplete
                    v-model="field.type"
                    v-validate="'required'"
                    :name="'type' + counter"
                    :error-messages="errors.collect('type' + counter)"
                    data-vv-delay="500"
                    data-vv-as="type"
                    :items="fieldTypes"
                    label="Field Type"
                    item-text="name"
                    item-value="name"
                    class="my-0"
                    autocomplete="off"
                    @change="field.selections = []"
                  />
                </v-col>
                
                <v-col class="text-center">
                  <v-icon
                    color="error"
                    @click="deleteCustomFieldRow(counter)"
                  >
                    mdi-window-close
                  </v-icon>
                </v-col>

                <v-col
                  cols="6"
                  class="pb-0"
                />
                <v-col
                  v-if="field.type === 'Selection'"
                  cols="5"
                  class="py-0 ml-4"
                >
                  <v-combobox
                    v-model="field.selections"
                    v-validate="'required'"
                    :name="'selection' + counter"
                    :error-messages="errors.collect('selection' + counter)"
                    data-vv-delay="500"
                    data-vv-as="selection"
                    label="Selections"
                    :placeholder="field.name ? 'Type to add selections for ' + field.name : 'Type to add selections'"
                    hint="Type and press enter to add a new selection"
                    persistent-hint
                    outlined
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    clearable
                    append-icon=""
                    autocomplete="off"
                  />
                </v-col>
              </v-row>
              <v-divider />
            </v-col>
          </v-row>
        </v-container>
      </form>

      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          color="hrissecondary"
          class="white--text"
          @click="submitForm"
        >
          Update
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  import { mapState } from 'vuex'
  import { VMoney } from 'v-money'
  import { TiptapVuetify, Bold, Italic, Underline, BulletList, OrderedList, ListItem, HardBreak } from 'tiptap-vuetify'

  export default {
    directives: { money: VMoney },
    $_veeValidate: {
      validator: 'new', 
    },
    components: {
      TiptapVuetify,
    },
    props: {
      claimType: {
        type: Object,
        required: true,
      },
    },
    data () {
      return {
        dialog: false,
        progressing: false,
        expenseTypes: [
          { 'name': 'Generic', 'description': 'Expense where the amount keyed is the reimbursement amount' },
          { 'name': 'Mileage', 'description': 'Expense that require the distance travelled in KM' },
          { 'name': 'Per unit allowance', 'description': 'Expense that based on daily rates/transaction, rates are the same for all job categories' },
        ],
        claimModels: ['Employee', 'Department'],
        localClaimType: null,
        expiryDateMenu: false,
        money: {
          decimal: '.',
          thousands: ',',
          precision: 2,
          masked: false, /* doesn't work with directive */
        },
        fieldTypes: ['Text', 'Number', 'Date', 'Selection'],
        extensions: [
          Bold,
          Italic,
          Underline,
          ListItem,
          BulletList,
          OrderedList,
          HardBreak,
        ],
      }
    },
    computed: {
      ...mapState('lookup', ['claim_unit_of_measurement']),
    },
    watch: {
      dialog: function (value) {
        if (!value) return

        this.localClaimType = {
          ...this.claimType,
          details: JSON.parse(JSON.stringify(this.claimType.details)),
          custom_fields: JSON.parse(JSON.stringify(this.claimType.custom_fields)),
        }

        this.localClaimType.expense_type = this.localClaimType.expense_type ? this.capitalizeFirstLetter(this.localClaimType.expense_type) : null
        this.localClaimType.claimed_by = this.localClaimType.claimed_by ? this.capitalizeFirstLetter(this.localClaimType.claimed_by) : null

        if (this.localClaimType.custom_fields.length > 0) {
          this.localClaimType.custom_fields.map(field => {
            field.selections = field.selections.map(selection => selection.name)
          })
        }

        if (!this.claim_unit_of_measurement || this.claim_unit_of_measurement.length === 0) {
          this.filteredUnits = []
        } else {
          if (this.localClaimType.expense_type === 'Mileage') {
            this.filteredUnits = this.claim_unit_of_measurement.filter(u => u.grouping === 'distance_unit')
          } else {
            this.filteredUnits = this.claim_unit_of_measurement.filter(u => u.grouping === 'allowance_unit')
          }
        }
      },
      'localClaimType.expense_type': function (value) {
        if (!this.claim_unit_of_measurement || this.claim_unit_of_measurement.length === 0) {
          this.filteredUnits = []
        } else {
          if (value === 'Mileage') {
            this.filteredUnits = this.claim_unit_of_measurement.filter(u => u.grouping === 'distance_unit')
          } else {
            this.filteredUnits = this.claim_unit_of_measurement.filter(u => u.grouping === 'allowance_unit')
          }
        }
      },
      'localClaimType.declaration_text': function (value) {
        if (this.localClaimType && this.localClaimType.requires_declaration) {
          this.validateDeclarationText(value)
        }
      },
    },
    methods: {
      formatDeclarationText () {
        if (!this.localClaimType.declaration_text) return ''

        return this.localClaimType.declaration_text
          // unwrap <p> tags inside <li>
          .replace(/<li>\s*<p>(.*?)<\/p>\s*<\/li>/g, '<li>$1</li>')
          // replace </p><p> boundaries with <br> (preserve line breaks)
          .replace(/<\/p>\s*<p>/g, '<br>')
          // remove remaining <p> and </p>
          .replace(/<\/?p>/g, '')
      },
      validateDeclarationText () {
        const cleanedHtml = this.formatDeclarationText()

        const text = cleanedHtml.replace(/<[^>]*>/g, '').trim()

        if (!text) {
          // Manually set error in vee-validate
          this.errors.add({ field: 'declarationText', msg: 'Declaration text is required' })
          return false
        } else {
          this.errors.remove('declarationText')
          return true
        }
      },
      capitalizeFirstLetter (str) {
        return str.charAt(0).toUpperCase() + str.slice(1)
      },
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            const declarationValid = this.localClaimType.requires_declaration ? this.validateDeclarationText() : true

            if (!result || !declarationValid) return 
        
            this.progressing = true

            this.$api.claimTypes().update(this.localClaimType.id, this.prepareFormData())
              .then(res => {
                if (res.status === 200 || res.status === 201) {
                  this.$emit('updated')              
                  this.$snack({ message: 'Claim type updated' })
                  this.$resetData()
                  this.dialog = false                 
                }
              })
              .catch((error) => {
                if (error.response.status === 400) {
                  if (this.localClaimType.expense_type !== 'Generic' && this.localClaimType.details.length <= 0) {
                    this.addRow()
                  }

                  this.$snack({ 
                    color: 'error',
                    message: error.response.data.message, 
                  })
                } else {
                  this.$snack({
                    color: 'error',
                    message: 'Failed to update claim type',
                  })
                }
              })
              .then(() => {
                this.progressing = false
              })
          })
      },
      prepareFormData () {
        return {
          name: this.localClaimType.name,
          expense_type: this.localClaimType.expense_type.toLowerCase(),
          claimed_by: this.localClaimType.claimed_by.toLowerCase(),
          requires_document: this.localClaimType.requires_document,
          requires_confirmation: this.localClaimType.requires_confirmation,
          is_description_mandatory: this.localClaimType.is_description_mandatory,
          requires_receipt: this.localClaimType.requires_receipt,
          allow_advance_purchase: this.localClaimType.requires_receipt ? this.localClaimType.allow_advance_purchase : 0,
          has_limit: this.localClaimType.has_limit,
          allow_exceed_limit: this.localClaimType.has_limit ? this.localClaimType.allow_exceed_limit : 0,
          allow_higher_level_approval: this.localClaimType.allow_higher_level_approval,
          requires_declaration: this.localClaimType.requires_declaration,
          declaration_text: this.localClaimType.requires_declaration ? this.formatDeclarationText() : null,
          details: this.localClaimType.expense_type !== 'Generic' ? this.localClaimType.details : [],
          custom_fields: this.localClaimType.custom_fields,
        }
      },
      addRow () {
        this.localClaimType.details.push({ name: '', amount: 0.00, unit_of_measurement: '' })
      },
      deleteRow (counter) {
        this.localClaimType.details.splice(counter, 1)
      },
      addCustomFieldRow () {
        this.localClaimType.custom_fields.push({ name: '', is_mandatory: true, type: null })
      },
      deleteCustomFieldRow (counter) {
        this.localClaimType.custom_fields.splice(counter, 1)
      },
    },
  }
</script>

<style scoped>
.text-editor-toolbar-height>>> .v-toolbar__content{
  height: unset !important;
  min-height: 42px;
}
.text-editor-toolbar-height>>>.v-sheet.v-sheet--tile.theme--light.v-toolbar.v-toolbar--dense.v-toolbar--flat.grey.lighten-4{
  min-height:unset !important;
}
</style>
